import React from 'react';
import { clsx } from 'clsx';
import { ButtonProps } from '../../types';

/**
 * Button component with multiple variants and sizes
 * 
 * @param {ButtonProps} props - Button component props
 * @returns {JSX.Element} Button component
 * 
 * @example
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className,
  children,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-300 ease-agentic focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-agentic-primary-blue text-agentic-white hover:bg-agentic-primary-blue-dark focus:ring-agentic-primary-blue',
    secondary: 'bg-transparent text-agentic-primary-blue border border-agentic-primary-blue hover:bg-agentic-primary-blue hover:text-agentic-white focus:ring-agentic-primary-blue',
    ghost: 'bg-transparent text-agentic-text-secondary hover:bg-agentic-gray-100 hover:text-agentic-text-primary focus:ring-agentic-gray-300',
    danger: 'bg-agentic-accent-red text-agentic-white hover:bg-red-600 focus:ring-agentic-accent-red',
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className
  );

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

